import React, { FC, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { getPollProxies } from '@utils/Proxy';
import { useAuth } from '@hooks/Auth';
import useSwr, { mutate } from 'swr';

import PollPicker from '@components/modules/PollPicker';
import { FormattedPoll } from '@utils/Poll';
import Card from '@components/elements/Card';
import {
  assignProxyPoll,
  listCompanyUsers,
  unassignProxyPoll,
} from '@sdk/managers/UsersManager';
import { CompanyRole } from '@sdk/API';
import Toggle from '@components/elements/Toggle';

const CompanyProxyList: FC = () => {
  const { dbUser, company, loading } = useAuth();
  const [modal, setModal] = useState<boolean>(false);
  const hideModal = useCallback(() => setModal(false), [setModal]);
  const showModal = useCallback(() => setModal(true), [setModal]);
  const [poll, setPoll] = useState<FormattedPoll>();

  const fetchProxies = useSwr(
    dbUser?.username ? ['/admin/proxies', dbUser.username] : null,
    async () => {
      if (!company) {
        if (loading === false) throw Error('User does not own any company.');
        return;
      }

      const proxies: any = await listCompanyUsers(
        company.companyID,
        CompanyRole.PROXY
      );
      return proxies.data?.result?.items || [];
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch proxies', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const pollProxies = useSwr(
    dbUser?.id && poll?.id ? ['/admin/proxy/poll', poll.id] : null,
    getPollProxies,
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch proxies', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const onPollSelect = useCallback(
    (poll: FormattedPoll) => {
      setPoll(poll);
    },
    [setPoll]
  );

  const onProxyToggle = useCallback(
    async (value: boolean, userId: string) => {
      if (!poll) return toast.error('Poll not selected');

      if (value) {
        const res = await toast.promise(
          (async () => {
            const res = await assignProxyPoll(userId, poll.id);
            await mutate(['/admin/proxy/poll', poll.id]);
            return res;
          })(),
          {
            loading: 'Assigning Proxy to Poll...',
            success: 'Assigned Proxy to Poll',
            error: (err) => {
              console.log('Failed to assign proxy', err);
              return err.error || err.message || 'Failed to assign proxy';
            },
          }
        );

        console.log(res);
      } else {
        const res = await toast.promise(
          (async () => {
            const res = await unassignProxyPoll(userId, poll.id);
            await mutate(['/admin/proxy/poll', poll.id]);
            return res;
          })(),
          {
            loading: 'Unassigning Proxy from Poll...',
            success: 'Unassigned Proxy from Poll',
            error: (err) => {
              console.log('Failed to unassign proxy', err);
              return err.error || err.message || 'Failed to unassign proxy';
            },
          }
        );

        console.log(res);
      }
    },
    [poll]
  );

  console.log('company proxies', fetchProxies.data, pollProxies.data);
  return (
    <>
      <div>
        <h2 className="mb-2 text-white uppercase">Choose Poll</h2>
        <PollPicker onChange={onPollSelect} />
        {poll?.id && (
          <Link to={`/poll/${poll.id}`}>
            <h3 className="my-2 underline uppercase text-yellow">View poll</h3>
          </Link>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-white uppercase">Assign proxy to poll</h2>
        <Card shadowColor="yellow" className="p-4 text-center">
          {!poll ? (
            'No Poll selected'
          ) : (fetchProxies.data?.length || 0) > 0 ? (
            <div className="flex flex-col space-y-2">
              {fetchProxies.data?.map((proxy) => (
                <div
                  key={`poll-proxy-${proxy.id}`}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <div>
                      <Toggle
                        value={
                          !!pollProxies.data?.find(
                            (p) => p.user.id === proxy.userID
                          ) || false
                        }
                        onChange={(v) => {
                          console.log(v);
                          onProxyToggle(v, proxy.userID);
                        }}
                        activeClassName="bg-magenta"
                      />
                    </div>
                    <div>{proxy.user.email}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            'No proxies assigned'
          )}
        </Card>
      </div>
    </>
  );
};

export default CompanyProxyList;
